import { useMemo } from 'react';
import { ProductUsp } from '@product/ProductUsp/ProductUsp';
import { DeliveryInfo } from '@common/DeliveryInfo/DeliveryInfo';
import { getLocale, type DeliveryTime } from '@libs/constants';
import { useTranslation } from 'next-i18next';
import { Badge } from '@common/Badge/Badge';
import { Link } from '@common/Link/Link';
import type { DeliveryTimeFragment } from '@generated/graphql';
import { WidenImage } from '@common/Widen/WidenImage';
import { formatPrice } from '@libs/product-price-utils';
import type { WidenMediaElement } from '@libs/widen-utils';
import type { AggregatedProductPrice } from '@models/enhanced-fragments';
import type { GraphQlPriceType } from '@models/graphql';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useRouter } from 'next/router';
import { getPriceQuantityUnitLabelDeprecated } from '@libs/product.helper';

export interface AlternativeProductProps {
  title: string;
  slug: string;
  singleUsp: string;
  new: boolean;
  priceType: GraphQlPriceType;
  image: WidenMediaElement | null;
  prices: AggregatedProductPrice | null;
  deliveryTimes: DeliveryTimeFragment[];
  // eslint-disable-next-line react/no-unused-prop-types
  productId?: string | null;
  productIdDisplayName?: string | null;
  primaryCategoryCode?: string;
}

export const AlternativeProduct = ({
  title,
  slug,
  singleUsp,
  new: isNew,
  priceType,
  image,
  prices,
  deliveryTimes,
  productIdDisplayName,
  primaryCategoryCode,
}: AlternativeProductProps) => {
  const router = useRouter();
  const locale = getLocale(router);
  // generate Link Url
  // include variant sku if preselected variant exists
  const linkTarget = useMemo(
    () =>
      `/product/${slug}${
        productIdDisplayName ? `?${new URLSearchParams({ artnr: productIdDisplayName }).toString()}` : ''
      }`,
    [slug, productIdDisplayName]
  );

  const breakpoint = useBreakpoints();

  const { t, i18n } = useTranslation(['category/productTeaser', 'category/productTeaserPriceBox']);

  const priceUnitDisplayLabel = getPriceQuantityUnitLabelDeprecated(locale, priceType, primaryCategoryCode);
  return (
    <div className="flex-auto w-full">
      <div data-testid="product-teaser-loading">
        <div className="flex flex-col lg:flex-row lg:mr-8">
          <div className="flex flex-col lg:flex-row">
            <div className="w-full">
              {isNew && <Badge text={t('PRODUCT_OVERVIEW.NEW_PRODUCT', { ns: 'category/productTeaser' })} />}
              {image && (
                <Link href={linkTarget} flex={false}>
                  <div className="relative">
                    <WidenImage
                      data-testid="suitable-product-image"
                      width={breakpoint.xl ? 220 : 190}
                      height={165}
                      layout="fixed"
                      alt={image.description ?? title}
                      shortCode={image.shortCode}
                      filename={image.filename}
                    />
                  </div>
                </Link>
              )}
              <Link href={linkTarget}>
                <h2 className="text-sm hover:font-normal">{title}</h2>
              </Link>
              {productIdDisplayName && <h3 className="text-sm">{productIdDisplayName}</h3>}
              <div className="text-sm text-gray-300	block mt-2">
                <ProductUsp removeLinks usp={singleUsp} singleUsp />
              </div>

              <div className="text-sm text-black">
                {(prices && prices.regularPrice && prices.regularPrice > 0 && (
                  <span className="block  mt-2" data-testid="product-total-price">
                    {`${formatPrice(prices.regularPrice, i18n.language, prices.currency)}/${t(priceUnitDisplayLabel, {
                      ns: 'category/productTeaserPriceBox',
                    })}`}
                  </span>
                )) || (
                  <span className="block">
                    {t('PRODUCT_OVERVIEW.PRICE_BOX.PRICE_ON_REQUEST', { ns: 'category/productTeaserPriceBox' })}
                  </span>
                )}
              </div>
              {deliveryTimes?.[0] && deliveryTimes?.[0]?.code !== undefined && deliveryTimes?.[0]?.code !== null && (
                <div className="my-4 text-sm">
                  <DeliveryInfo
                    deliveryTimeCode={deliveryTimes?.[0]?.code.toString() as DeliveryTime}
                    deliveryTimeText={deliveryTimes?.[0]?.label || undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
